import React from "react";
import Logo from "../components/Logo";
import BackButton from "../components/BackButton";
import { Button } from "../components/ui/button";
import { WaitlistDialog } from "@/components/WaitlistDialog";
import { useQuery } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, getFirestore } from "firebase/firestore";
import app from "../../firebase/firebaseConfig";

const db = getFirestore(app);

type EateryType = "Restaurant" | "Cafe" | "Dessert";

type Eatery = {
  name: string;
  type: string;
  City: string;
  max_boost_card: number;
  status?: string;
  createdAt?: any;
};

type BoostCard = {
  status: string;
  eateryRef: any;
  qrCode: string;
  batch: number;
  userRef: any | null;
  userAddedTime: string | null;
};

type EateryWithBoostCards = {
  eatery_name: string;
  available_cards: number;
  active_cards: number;
  draft_cards: number;
  max_boost_cards: number;
  type: EateryType;
  city: string;
  is_arriving_soon: boolean;
  is_new: boolean;
};

const getTypeColor = (type: EateryType) => {
  switch (type) {
    case "Restaurant":
      return "#FEC6A1";
    case "Cafe":
      return "#FEF7CD";
    case "Dessert":
      return "#D3E4FD";
    default:
      return "#FEC6A1";
  }
};

const renderBoostCardStatus = (eatery: EateryWithBoostCards) => {
  if (eatery.available_cards > 0) {
    return (
      <span className="inline-flex px-2.5 py-0.5 bg-[#FFE6CC] text-[#E65C00] rounded-full text-xs font-bold whitespace-nowrap">
        Current Batch: {eatery.available_cards}/{eatery.active_cards} Remaining
      </span>
    );
  } else if (eatery.is_arriving_soon) {
    return (
      <span className="inline-flex px-2.5 py-0.5 bg-[#F2FCE2] rounded-full text-xs whitespace-nowrap text-slate-950 font-bold">
        Arriving soon!
      </span>
    );
  } else if (eatery.active_cards === eatery.max_boost_cards) {
    return (
      <span className="inline-flex px-2.5 py-0.5 bg-[#FFDEE2] text-black rounded-full text-xs font-bold whitespace-nowrap">
        All Boost Cards Issued
      </span>
    );
  } else {
    return (
      <span className="inline-flex px-2.5 py-0.5 bg-[#D3E4FD] text-black rounded-full text-xs font-bold whitespace-nowrap">
        Sold Out!
      </span>
    );
  }
};

const getInfoRoute = (eatery: EateryWithBoostCards) => {
  if (eatery.is_arriving_soon) {
    return '/arriving-soon';
  } else if (eatery.active_cards === eatery.max_boost_cards) {
    return '/no-more-boost-cards';
  } else if (eatery.available_cards === 0) {
    return '/boost-cards-on-the-way';
  }
  return null;
};

const fetchEateriesAndBoostCards = async (): Promise<EateryWithBoostCards[]> => {
  const eateriesSnapshot = await getDocs(collection(db, "Eateries"));
  const eateries: any[] = [];
  eateriesSnapshot.forEach(doc => {
    eateries.push({ id: doc.id, ...doc.data() });
  });

  const activeEateries = eateries.filter(eatery => eatery.status && eatery.status.toLowerCase() === "active");

  const boostCardsSnapshot = await getDocs(collection(db, "QR_Codes"));
  const boostCards: any[] = [];
  boostCardsSnapshot.forEach(doc => {
    boostCards.push({ id: doc.id, ...doc.data() });
  });

  console.log("Eateries:", activeEateries);
  console.log("Boost Cards:", boostCards);

  const processedEateries = activeEateries.map((eatery) => {
    const createdAtDate = eatery.createdAt ? eatery.createdAt.toDate() : null;
    const now = new Date();
    const isNew = createdAtDate ? (now.getTime() - createdAtDate.getTime()) <= (7 * 24 * 60 * 60 * 1000) : false;

    const eateryBoostCards = boostCards.filter((card) => card.eateryRef && card.eateryRef.id === eatery.id);

    if (eateryBoostCards.length === 0) {
      console.log(`Eatery: ${eatery.name} - No boost cards at all, marking as arriving soon`);
      return {
        eatery_name: eatery.name,
        available_cards: 0,
        active_cards: 0,
        draft_cards: 0,
        max_boost_cards: eatery.max_boost_card || 0,
        type: (eatery.type as EateryType) || "Restaurant",
        city: eatery.City || 'Unknown',
        is_arriving_soon: true,
        is_new: isNew
      };
    }

    const batches = [...new Set(eateryBoostCards.map(card => card.batch))].sort((a, b) => a - b);
    const highestBatch = Math.max(...batches);

    const highestBatchCards = eateryBoostCards.filter(card => card.batch === highestBatch);
    const availableCards = highestBatchCards.filter(card => card.status?.toLowerCase() === 'inactive').length;
    const totalCards = highestBatchCards.length;

    const previousBatchesAllActive = batches.length === 1 || batches.filter(batch => batch < highestBatch).every(batch => {
      const batchCards = eateryBoostCards.filter(card => card.batch === batch);
      return batchCards.every(card => card.status?.toLowerCase() === 'active');
    });

    const highestBatchAllDraft = eateryBoostCards.filter(card => card.batch === highestBatch).every(card => card.status?.toLowerCase() === 'draft');

    const isArrivingSoon = batches.length > 0 && ((batches.length === 1 && highestBatchAllDraft) || (batches.length > 1 && previousBatchesAllActive && highestBatchAllDraft));

    console.log(`Eatery: ${eatery.name}`);
    console.log(`- ID: ${eatery.id}`);
    console.log(`- Max Boost Cards: ${eatery.max_boost_card}`);
    console.log(`- Available Cards (Inactive in highest batch): ${availableCards}`);
    console.log(`- Total Cards in highest batch: ${totalCards}`);
    console.log(`- Highest Batch: ${highestBatch}`);
    console.log(`- Previous Batches All Active: ${previousBatchesAllActive}`);
    console.log(`- Highest Batch All Draft: ${highestBatchAllDraft}`);
    console.log(`- Is Arriving Soon: ${isArrivingSoon}`);

    return {
      eatery_name: eatery.name,
      available_cards: availableCards,
      active_cards: totalCards,
      draft_cards: highestBatchCards.filter(card => card.status?.toLowerCase() === 'draft').length,
      max_boost_cards: eatery.max_boost_card || 0,
      type: (eatery.type as EateryType) || "Restaurant",
      city: eatery.City || 'Unknown',
      is_arriving_soon: isArrivingSoon,
      is_new: isNew
    };
  });
  return processedEateries.sort((a, b) => a.eatery_name.localeCompare(b.eatery_name));
};

const AvailableEateries = () => {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [selectedCity, setSelectedCity] = React.useState<string | null>(null);
  const [showAvailableOnly, setShowAvailableOnly] = React.useState(false);
  const [selectedEatery, setSelectedEatery] = React.useState<string | null>(null);
  const navigate = useNavigate();

  const { data: eateries, isLoading, error } = useQuery({
    queryKey: ['eateries-with-boost-cards'],
    queryFn: fetchEateriesAndBoostCards
  });

  const cities = React.useMemo(() => {
    if (!eateries) return [];
    const uniqueCities = [...new Set(eateries.map(eatery => eatery.city))];
    return uniqueCities.sort();
  }, [eateries]);

  const filteredEateries = React.useMemo(() => {
    if (!eateries) return [];
    let filtered = eateries;

    if (selectedCity) {
      filtered = filtered.filter(eatery => eatery.city === selectedCity);
    }

    if (showAvailableOnly) {
      filtered = filtered.filter(eatery => eatery.available_cards > 0);
    }
    return filtered;
  }, [eateries, selectedCity, showAvailableOnly]);

  const handleNavigate = (path: string, state?: any) => {
    navigate(path, { state });
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setSelectedEatery(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center p-6">
        <p className="text-lg text-gray-600">Loading available eateries...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center p-6">
        <p className="text-lg text-red-600">Error loading eateries. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex flex-col items-center p-6">
      <BackButton />
      <div className="w-full max-w-4xl mx-auto">
        <Logo />
        <div className="mt-12 space-y-8">
          <h1 className="text-4xl font-bold text-center bg-gradient-to-r from-black to-gray-700 bg-clip-text text-transparent">
            Available Boost Cards by Location
          </h1>

          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 justify-center">
              <Button onClick={() => setShowAvailableOnly(false)} variant={!showAvailableOnly ? "default" : "outline"} className={!showAvailableOnly ? "bg-[#2563EB] hover:bg-[#1D4ED8] text-white" : ""}>
                All Eateries
              </Button>
              <Button onClick={() => setShowAvailableOnly(true)} variant={showAvailableOnly ? "default" : "outline"} className={showAvailableOnly ? "bg-[#2563EB] hover:bg-[#1D4ED8] text-white" : ""}>
                Available Only
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 justify-center">
              <Button onClick={() => setSelectedCity(null)} variant={!selectedCity ? "default" : "outline"} className={!selectedCity ? "bg-eatsboost-orange hover:bg-eatsboost-orange/90 text-white" : ""}>
                All Cities
              </Button>
              {cities.map(city => (
                <Button key={city} onClick={() => setSelectedCity(city)} variant={selectedCity === city ? "default" : "outline"} className={selectedCity === city ? "bg-eatsboost-orange hover:bg-eatsboost-orange/90 text-white" : ""}>
                  {city}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {filteredEateries.map(eatery => (
              <div key={eatery.eatery_name} className="p-4 sm:p-6 bg-white rounded-lg shadow-md border border-gray-200 transition-all hover:shadow-lg relative">
                {eatery.is_new && (
                  <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2">
                    <span className="inline-flex px-2.5 py-0.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full text-xs font-bold shadow-md animate-pulse">
                      NEW
                    </span>
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                      {eatery.eatery_name}
                    </h3>
                    <div className="inline-flex items-center gap-x-2 text-gray-500">
                      <span className="text-gray-500">•</span>
                      <span className="text-sm">
                        {eatery.city}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-2 max-w-[80%]">
                      {renderBoostCardStatus(eatery)}
                      
                      {getInfoRoute(eatery) && (
                        <button 
                          onClick={() => handleNavigate(getInfoRoute(eatery)!, { fromEateriesPage: true })} 
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-full text-xs font-medium transition-colors whitespace-nowrap"
                        >
                          <Info className="w-3 h-3" />
                          <span>Tap for Info</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col items-center space-y-4">
            <p className="text-lg text-center text-gray-700">
              We just launched, so our selection is still growing - but the <span className="font-bold">power</span> is yours! If you don't see an eatery, tell us! We'll bring it to <span className="font-bold">EatsBoost</span> and notify you.
            </p>
            <Button onClick={() => setIsDialogOpen(true)} className="bg-eatsboost-orange hover:bg-eatsboost-orange/90 text-white py-6 text-lg" size="lg">
              Request Eatery
            </Button>
          </div>
        </div>
      </div>

      <WaitlistDialog 
        open={isDialogOpen} 
        onOpenChange={handleDialogOpenChange} 
        tableName={selectedEatery ? "waitlist" : "eateries_requests"}
      />
    </div>
  );
};

export default AvailableEateries;
